import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  checkoutRestartFailureIsTerminal,
  checkoutRestartURL,
  customerAbandonmentAvailable,
} from './customer-abandonment'
import {
  copyRestartMethodChoice,
  rememberRestartMethodChoice,
  restartMethodReference,
} from './restart-method-choice'
import {
  checkoutMutationAuthorizationUsable,
  saveCheckoutRestartIntent,
  loadCheckoutRestartIntent,
  clearCheckoutRestartIntent,
  captureCheckoutEntry,
  clearCheckoutOperationKey,
  clearSuccessorMethodIntent,
  digestCheckoutOperationInput,
  getOrCreateCheckoutOperationKey,
  loadCheckoutAuthorization,
  loadSuccessorMethodIntent,
  parseCheckoutEntry,
  replaceCheckoutReturnWithSessionLocation,
  saveCheckoutAuthorization,
  saveSuccessorMethodIntent,
} from './runtime'
import {
  restartCheckoutSession,
  cancelCheckoutExecution,
  cancelCheckoutSession,
  completeCheckoutSession,
  createCheckoutPurchaseSuccessor,
  exchangeCheckoutAuthorization,
  getCheckoutExecution,
  getCheckoutPaymentMethods,
  getCheckoutSession,
  issueCheckoutResultAuthorization,
  reconcileCheckoutExecutionPayment,
  reviseCheckoutSession,
  startCheckoutExecution,
} from '../api/checkout-session'
import { getCurrencyList, getGeoInfo } from '../api/endpoints'
import { ApiRequestError } from '../api/types'

import type {
  CheckoutExecution,
  CheckoutRestartRequest,
  CheckoutBillingJurisdiction,
  CheckoutMethod,
  CheckoutPaymentMethodsResponse,
  CheckoutPurchaseSuccessorResponse,
  CheckoutSession,
  CheckoutSessionAuthorization,
  CheckoutStartExecutionRequest,
} from '../api/checkout-session-types'
import type { CurrencyItem } from '../api/types'
import type { CurrencyOption } from '../checkout-ui-types'

const CHECKOUT_POLL_INITIAL_DELAY_MS = 1500
const CHECKOUT_POLL_MAX_DELAY_MS = 12000
const CHECKOUT_POLL_BACKOFF_RATE = 1.8
const CHECKOUT_POLL_MAX_CONSECUTIVE_FAILURES = 5
const CHECKOUT_STATUS_POLL_MAX_ATTEMPTS = 12

function flattenMethods(response: CheckoutPaymentMethodsResponse | null): CheckoutMethod[] {
  const methods = response?.groups.flatMap((group) => group.methods) ?? []
  return methods
    .map((method, originalIndex) => ({ method, originalIndex }))
    .sort((left, right) => {
      return (
        left.method.sort_order - right.method.sort_order || left.originalIndex - right.originalIndex
      )
    })
    .map(({ method }) => method)
}

function methodReferenceForActiveExecution(
  methods: CheckoutMethod[],
  methodBinding: string | null
): string | null {
  if (!methodBinding) {
    return null
  }
  const matches = methods.filter((method) => method.method_binding === methodBinding)
  const [match] = matches
  return matches.length === 1 && match ? match.method_reference : null
}

function preferredCountry(): string {
  const browserLanguage = typeof navigator === 'undefined' ? '' : navigator.language
  const languageRegion = browserLanguage.split('-')[1]?.trim().toUpperCase()
  return languageRegion && /^[A-Z]{2}$/.test(languageRegion) ? languageRegion : 'US'
}

function normalizeCountryCode(value: string | null | undefined): string | null {
  const normalized = value?.trim().toUpperCase() ?? ''
  return /^[A-Z]{2}$/.test(normalized) ? normalized : null
}

function normalizeCurrencyCode(value: string): string {
  return value.trim().toUpperCase()
}

function checkoutCurrencyOptions(session: CheckoutSession | null): CurrencyOption[] {
  if (!session) {
    return []
  }
  const seen = new Set<string>()
  return session.pricing_input.available_currencies.flatMap((value) => {
    const code = normalizeCurrencyCode(value)
    if (!code || seen.has(code)) {
      return []
    }
    seen.add(code)
    return [{ code, label: code }]
  })
}

function initialCurrencyForCountry(
  session: CheckoutSession,
  currencies: CurrencyItem[],
  countryCode: string
): string {
  if (session.scenario === 'subscription_change') {
    return normalizeCurrencyCode(session.pricing_input.currency)
  }
  const metadata = new Map(
    currencies.map((currency) => [normalizeCurrencyCode(currency.code), currency] as const)
  )
  for (const configuredCurrency of session.pricing_input.available_currencies) {
    const code = normalizeCurrencyCode(configuredCurrency)
    const countries = metadata.get(code)?.countries ?? []
    if (countries.some((country) => normalizeCountryCode(country) === countryCode)) {
      return code
    }
  }
  return normalizeCurrencyCode(session.pricing_input.currency)
}

function billingJurisdictionForCountry(
  jurisdiction: CheckoutBillingJurisdiction,
  countryCode: string
): CheckoutBillingJurisdiction {
  if (normalizeCountryCode(jurisdiction.country_code) === countryCode) {
    return jurisdiction
  }
  return { country_code: countryCode }
}

function executionNeedsPolling(execution: CheckoutExecution | null): boolean {
  return (
    execution?.status === 'prepared' ||
    execution?.status === 'dispatch_pending' ||
    execution?.status === 'processing' ||
    execution?.status === 'cancel_requested' ||
    execution?.status === 'canceling' ||
    execution?.status === 'unknown'
  )
}

function returnedExecutionNeedsConfirmation(execution: CheckoutExecution | null): boolean {
  return execution?.status === 'requires_action' || executionNeedsPolling(execution)
}

function sessionNeedsPolling(session: CheckoutSession | null): boolean {
  return (
    session?.status === 'settling' ||
    session?.status === 'closing' ||
    session?.status === 'completing' ||
    session?.status === 'compensating'
  )
}

function successfulReturnNeedsSessionPolling(
  session: CheckoutSession | null,
  confirmationExecutionNo: string | null,
  confirmedExecution: CheckoutExecution | null
): boolean {
  return Boolean(
    session &&
    confirmationExecutionNo &&
    !sessionIsTerminal(session) &&
    session.status !== 'requires_attention' &&
    confirmedExecution?.execution_no === confirmationExecutionNo &&
    confirmedExecution.status === 'succeeded'
  )
}

function activeExecutionAfterSessionRefresh(
  session: CheckoutSession,
  confirmedExecution?: CheckoutExecution
): CheckoutExecution | null {
  const projectedExecution = session.active_execution
  if (
    !confirmedExecution ||
    confirmedExecution.status !== 'succeeded' ||
    sessionIsTerminal(session) ||
    (projectedExecution && projectedExecution.execution_no !== confirmedExecution.execution_no) ||
    (projectedExecution && !returnedExecutionNeedsConfirmation(projectedExecution))
  ) {
    return projectedExecution
  }
  return confirmedExecution
}

function checkoutPollDelay(attempt: number): number {
  const cappedAttempt = Math.min(attempt, 4)
  return Math.min(
    CHECKOUT_POLL_INITIAL_DELAY_MS * CHECKOUT_POLL_BACKOFF_RATE ** cappedAttempt,
    CHECKOUT_POLL_MAX_DELAY_MS
  )
}

function sessionIsTerminal(session: CheckoutSession | null): boolean {
  return (
    session?.status === 'completed' ||
    session?.status === 'compensated' ||
    session?.status === 'canceled' ||
    session?.status === 'expired'
  )
}

function sessionAllowsPaymentMethods(session: CheckoutSession): boolean {
  return (
    (session.status === 'open' && session.available_actions.includes('list_payment_methods')) ||
    (session.status === 'closing' && customerAbandonmentAvailable(session))
  )
}

export interface UseCheckoutSessionResult {
  entry: ReturnType<typeof captureCheckoutEntry>
  authorization: CheckoutSessionAuthorization | null
  session: CheckoutSession | null
  methods: CheckoutMethod[]
  selectedMethod: CheckoutMethod | null
  activeExecution: CheckoutExecution | null
  paymentSwitch: ({ executionNo: string } & NonNullable<CheckoutExecution['payment_switch']>) | null
  countryCode: string
  currencyOptions: CurrencyOption[]
  isInitializing: boolean
  isLoadingMethods: boolean
  isStartingExecution: boolean
  isRevising: boolean
  isRestarting: boolean
  hasPendingRestart: boolean
  restart: (preferredMethodReference?: string) => Promise<string>
  paymentStatusTimedOut: boolean
  error: string | null
  selectMethod: (methodReference: string) => void
  startExecution: (
    request: Omit<CheckoutStartExecutionRequest, 'session_revision' | 'method_reference'>
  ) => Promise<CheckoutExecution>
  createPurchaseSuccessor: (
    sourceExecutionNo: string,
    reason: 'selection_changed' | 'link_unavailable'
  ) => Promise<CheckoutPurchaseSuccessorResponse>
  cancelExecution: (executionNo: string) => Promise<void>
  cancelSession: () => Promise<void>
  refresh: () => Promise<void>
  retryPaymentStatusPolling: () => void
  reconcileExecution: (
    executionNo: string,
    verifyApprovalLink?: boolean
  ) => Promise<CheckoutExecution>
  revise: (
    currency: string,
    quantity: number,
    couponCode: string | null,
    customerEmail: string
  ) => Promise<void>
  complete: () => Promise<void>
  issueResultAuthorization: () => Promise<string>
}

export function useCheckoutSession(
  locale: string,
  capturedEntry?: ReturnType<typeof captureCheckoutEntry>
): UseCheckoutSessionResult {
  const entry = useMemo(() => capturedEntry ?? captureCheckoutEntry(), [capturedEntry])
  const [authorization, setAuthorization] = useState<CheckoutSessionAuthorization | null>(null)
  const [session, setSession] = useState<CheckoutSession | null>(null)
  const [methodResponse, setMethodResponse] = useState<CheckoutPaymentMethodsResponse | null>(null)
  const [selectedMethodReference, setSelectedMethodReference] = useState<string | null>(null)
  const [activeExecution, setActiveExecution] = useState<CheckoutExecution | null>(null)
  const [countryCode, setCountryCode] = useState(preferredCountry)
  const [isInitializing, setInitializing] = useState(true)
  const [isLoadingMethods, setLoadingMethods] = useState(false)
  const [isStartingExecution, setStartingExecution] = useState(false)
  const [isRevising, setRevising] = useState(false)
  const [isRestarting, setRestarting] = useState(false)
  const [hasPendingRestart, setHasPendingRestart] = useState(false)
  const restartingRef = useRef(false)
  const [isReconcilingExecution, setReconcilingExecution] = useState(false)
  const [paymentStatusTimedOut, setPaymentStatusTimedOut] = useState(false)
  const [confirmationExecutionNo, setConfirmationExecutionNo] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const mountedRef = useRef(true)
  const startingExecutionRef = useRef(false)
  const revisingRef = useRef(false)
  const authorizationExchangeRef = useRef<Promise<CheckoutSessionAuthorization> | null>(null)
  const cancelReturnRef = useRef<Promise<void> | null>(null)
  const methodsRequestGenerationRef = useRef(0)
  const statusPollingRef = useRef({
    target: null as string | null,
    attempts: 0,
    consecutiveFailures: 0,
  })

  const methods = useMemo(
    () =>
      methodResponse?.session_revision === session?.revision ? flattenMethods(methodResponse) : [],
    [methodResponse, session?.revision]
  )
  const selectedMethod = useMemo(
    () => methods.find((method) => method.method_reference === selectedMethodReference) ?? null,
    [methods, selectedMethodReference]
  )
  const paymentSwitch =
    activeExecution?.selection_status === 'active' &&
    activeExecution.payment_switch &&
    selectedMethod &&
    activeExecution.method_binding !== selectedMethod.method_binding
      ? { ...activeExecution.payment_switch, executionNo: activeExecution.execution_no }
      : null
  const currencyOptions = useMemo(() => checkoutCurrencyOptions(session), [session])
  const paymentMethodsAllowed = Boolean(session && sessionAllowsPaymentMethods(session))
  const paymentMethodsSelectionOnly =
    session?.status === 'closing' && customerAbandonmentAvailable(session)
  const sessionRevision = session?.revision ?? null
  const activeMethodBinding =
    session?.active_execution?.selection_status === 'active'
      ? session.active_execution.method_binding
      : null

  const readSession = useCallback(
    async (
      currentAuthorization: CheckoutSessionAuthorization,
      requestedCountryCode: string
    ): Promise<CheckoutSession> => {
      if (checkoutMutationAuthorizationUsable(currentAuthorization)) {
        return getCheckoutSession(
          entry.sessionNo,
          currentAuthorization.sessionToken,
          'mutation',
          requestedCountryCode
        )
      }
      return getCheckoutSession(
        entry.sessionNo,
        currentAuthorization.resultHandoffToken,
        'handoff',
        requestedCountryCode
      )
    },
    [entry.sessionNo]
  )

  const loadMethods = useCallback(
    async (
      currentAuthorization: CheckoutSessionAuthorization,
      nextCountryCode: string,
      expectedRevision: number,
      selectionOnly = false
    ) => {
      const requestGeneration = ++methodsRequestGenerationRef.current
      if (!checkoutMutationAuthorizationUsable(currentAuthorization)) {
        if (requestGeneration === methodsRequestGenerationRef.current) {
          setLoadingMethods(false)
          setMethodResponse(null)
        }
        return
      }
      setLoadingMethods(true)
      try {
        const response = await getCheckoutPaymentMethods(
          entry.sessionNo,
          currentAuthorization.sessionToken,
          nextCountryCode,
          locale,
          ...(selectionOnly ? ([true] as const) : [])
        )
        if (!mountedRef.current || requestGeneration !== methodsRequestGenerationRef.current) {
          return
        }
        if (response.session_revision !== expectedRevision) {
          setMethodResponse(null)
          setSelectedMethodReference(null)
          throw new Error('Checkout payment methods are stale')
        }
        const available = flattenMethods(response)
        setMethodResponse(response)
        const successorIntent = loadSuccessorMethodIntent(entry.sessionNo)
        if (successorIntent) {
          const matches = available.filter(
            (method) =>
              method.provider === successorIntent.provider &&
              method.pm_id === successorIntent.pm_id &&
              method.purchase_option_id === successorIntent.purchase_option_id
          )
          if (matches.length !== 1) {
            setSelectedMethodReference(null)
            throw new Error('CHECKOUT_SUCCESSOR_METHOD_UNAVAILABLE')
          }
          setSelectedMethodReference(matches[0]!.method_reference)
          return
        }
        setSelectedMethodReference((current) => {
          return available.some((method) => method.method_reference === current)
            ? current
            : (restartMethodReference(entry.sessionNo, available) ??
                available[0]?.method_reference ??
                null)
        })
      } catch (methodError) {
        if (requestGeneration === methodsRequestGenerationRef.current) {
          throw methodError
        }
      } finally {
        if (mountedRef.current && requestGeneration === methodsRequestGenerationRef.current) {
          setLoadingMethods(false)
        }
      }
    },
    [entry.sessionNo, locale]
  )

  const refreshSession = useCallback(
    async (confirmedExecution?: CheckoutExecution) => {
      if (!authorization) {
        return
      }
      const nextSession = await readSession(authorization, countryCode)
      if (!mountedRef.current) {
        return
      }
      setSession(nextSession)
      setActiveExecution(activeExecutionAfterSessionRefresh(nextSession, confirmedExecution))
      if (!sessionAllowsPaymentMethods(nextSession)) {
        methodsRequestGenerationRef.current += 1
        setLoadingMethods(false)
        setMethodResponse(null)
        setSelectedMethodReference(null)
      }
    },
    [authorization, countryCode, readSession]
  )

  const refresh = useCallback(async () => {
    await refreshSession()
    if (mountedRef.current) setError(null)
  }, [refreshSession])

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return
      void refresh().catch(() => setError('CHECKOUT_PAYMENT_SWITCH_QUERY_FAILED'))
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [refresh])

  const retryPaymentStatusPolling = useCallback(() => {
    statusPollingRef.current = {
      target: activeExecution?.execution_no ?? entry.returnedExecutionNo,
      attempts: 0,
      consecutiveFailures: 0,
    }
    setError(null)
    setPaymentStatusTimedOut(false)
  }, [activeExecution?.execution_no, entry.returnedExecutionNo])

  const reconcileExecutionWithOperation = useCallback(
    async (
      executionNo: string,
      operation: string,
      verifyApprovalLink = false
    ): Promise<CheckoutExecution> => {
      if (!authorization) {
        throw new Error('Checkout authorization has expired')
      }
      const scope = checkoutMutationAuthorizationUsable(authorization) ? 'mutation' : 'handoff'
      const token =
        scope === 'mutation' ? authorization.sessionToken : authorization.resultHandoffToken
      const idempotencyKey = getOrCreateCheckoutOperationKey(entry.sessionNo, operation)
      const execution = verifyApprovalLink
        ? await reconcileCheckoutExecutionPayment(
            entry.sessionNo,
            executionNo,
            token,
            scope,
            idempotencyKey,
            true
          )
        : await reconcileCheckoutExecutionPayment(
            entry.sessionNo,
            executionNo,
            token,
            scope,
            idempotencyKey
          )
      clearCheckoutOperationKey(entry.sessionNo, operation)
      return execution
    },
    [authorization, entry.sessionNo]
  )

  const reconcileExecution = useCallback(
    async (executionNo: string, verifyApprovalLink = false) => {
      setConfirmationExecutionNo(executionNo)
      statusPollingRef.current = {
        target: executionNo,
        attempts: 0,
        consecutiveFailures: 0,
      }
      try {
        setReconcilingExecution(true)
        const execution = await reconcileExecutionWithOperation(
          executionNo,
          `reconcile:${executionNo}:${verifyApprovalLink ? 'approval-link' : 'payment'}`,
          verifyApprovalLink
        )
        if (mountedRef.current) setActiveExecution(execution)
        try {
          await refreshSession(execution)
        } catch (refreshError) {
          if (mountedRef.current) {
            setError(
              refreshError instanceof Error
                ? refreshError.message
                : 'Checkout status could not be refreshed'
            )
          }
        }
        return execution
      } finally {
        if (mountedRef.current) {
          setReconcilingExecution(false)
        }
      }
    },
    [reconcileExecutionWithOperation, refreshSession]
  )

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!entry.sessionNo) {
      setError('Checkout session is missing')
      setInitializing(false)
      return
    }

    const initialize = async () => {
      setInitializing(true)
      setError(null)
      try {
        let currentAuthorization = loadCheckoutAuthorization(entry.sessionNo)
        if (entry.authorizationCode) {
          if (!authorizationExchangeRef.current) {
            const operation = 'authorization-exchange'
            const idempotencyKey = getOrCreateCheckoutOperationKey(entry.sessionNo, operation)
            authorizationExchangeRef.current = exchangeCheckoutAuthorization(
              entry.sessionNo,
              entry.authorizationCode,
              idempotencyKey
            )
              .then((exchange) => {
                const exchangedAuthorization = saveCheckoutAuthorization(entry.sessionNo, exchange)
                clearCheckoutOperationKey(entry.sessionNo, operation)
                return exchangedAuthorization
              })
              .catch((exchangeError: unknown) => {
                authorizationExchangeRef.current = null
                throw exchangeError
              })
          }
          currentAuthorization = await authorizationExchangeRef.current
        }
        if (!currentAuthorization) {
          throw new Error('Checkout authorization has expired')
        }

        const pendingRestart = loadCheckoutRestartIntent(entry.sessionNo)
        if (pendingRestart) {
          setAuthorization(currentAuthorization)
          setHasPendingRestart(true)
          setRestarting(true)
          restartingRef.current = true
          try {
            const response = await restartCheckoutSession(
              entry.sessionNo,
              currentAuthorization.sessionToken,
              getOrCreateCheckoutOperationKey(
                entry.sessionNo,
                `restart:${pendingRestart.expected_revision}`
              ),
              pendingRestart
            )
            const successorURL = checkoutRestartURL(
              response,
              entry.sessionNo,
              window.location.origin
            )
            copyRestartMethodChoice(entry.sessionNo, response.checkout_session_no)
            window.location.replace(successorURL)
          } catch (restartError) {
            if (!checkoutRestartFailureIsTerminal(restartError)) {
              throw new ApiRequestError(
                'Checkout restart could not be confirmed',
                'CHECKOUT_PAYMENT_SWITCH_QUERY_FAILED'
              )
            }
            // 释放后原修改凭据已撤销，明确失败时仅用结果凭据读取服务端现状。
            const result = await getCheckoutSession(
              entry.sessionNo,
              currentAuthorization.resultHandoffToken,
              'handoff',
              preferredCountry()
            )
            clearCheckoutRestartIntent(entry.sessionNo)
            setHasPendingRestart(false)
            if (mountedRef.current) {
              setSession(result)
              setActiveExecution(result.active_execution)
              setError('CHECKOUT_PAYMENT_SWITCH_STATE_CHANGED')
            }
          } finally {
            restartingRef.current = false
            if (mountedRef.current) setRestarting(false)
          }
          return
        }

        const geoResponse = await getGeoInfo()
        const resolvedCountryCode =
          normalizeCountryCode(geoResponse.data?.code) ?? preferredCountry()
        const initialSession = await readSession(currentAuthorization, resolvedCountryCode)
        const currencyResponse = await getCurrencyList(initialSession.product_code)
        const currencyMetadata = currencyResponse.data ?? []
        const geoCountryCode = normalizeCountryCode(geoResponse.data?.code)
        const nextCountryCode =
          geoCountryCode ??
          normalizeCountryCode(initialSession.pricing_input.billing_jurisdiction.country_code) ??
          preferredCountry()
        let nextSession = initialSession
        if (
          geoCountryCode &&
          checkoutMutationAuthorizationUsable(currentAuthorization) &&
          initialSession.status === 'open' &&
          initialSession.revision === 1 &&
          initialSession.active_execution === null &&
          !loadSuccessorMethodIntent(entry.sessionNo)
        ) {
          const initialCurrency = initialCurrencyForCountry(
            initialSession,
            currencyMetadata,
            geoCountryCode
          )
          if (initialCurrency !== normalizeCurrencyCode(initialSession.pricing_input.currency)) {
            const operation = `initialize-currency:${initialSession.revision}:${geoCountryCode}:${initialCurrency}`
            const idempotencyKey = getOrCreateCheckoutOperationKey(entry.sessionNo, operation)
            await reviseCheckoutSession(
              entry.sessionNo,
              currentAuthorization.sessionToken,
              idempotencyKey,
              {
                expected_revision: initialSession.revision,
                currency: initialCurrency,
                quantity: initialSession.pricing_input.quantity,
                coupon_code: initialSession.pricing_input.coupon_code,
                billing_jurisdiction: billingJurisdictionForCountry(
                  initialSession.pricing_input.billing_jurisdiction,
                  geoCountryCode
                ),
                customer_email: initialSession.buyer_identity.email.trim().toLowerCase(),
              }
            )
            clearCheckoutOperationKey(entry.sessionNo, operation)
            nextSession = await readSession(currentAuthorization, nextCountryCode)
          }
        }
        if (!mountedRef.current) {
          return
        }
        setAuthorization(currentAuthorization)
        setCountryCode(nextCountryCode)
        setSession(nextSession)
        setActiveExecution(nextSession.active_execution)
      } catch (initializationError) {
        if (mountedRef.current) {
          setError(
            initializationError instanceof ApiRequestError && initializationError.code
              ? initializationError.code
              : initializationError instanceof Error
                ? initializationError.message
                : 'Failed to initialize checkout'
          )
        }
      } finally {
        if (mountedRef.current) {
          setInitializing(false)
        }
      }
    }

    void initialize()
  }, [entry.authorizationCode, entry.sessionNo, readSession])

  useEffect(() => {
    if (!authorization || sessionRevision === null) {
      return
    }
    if (!paymentMethodsAllowed) {
      methodsRequestGenerationRef.current += 1
      setLoadingMethods(false)
      setMethodResponse(null)
      setSelectedMethodReference(null)
      return
    }
    void loadMethods(
      authorization,
      countryCode,
      sessionRevision,
      paymentMethodsSelectionOnly
    ).catch((methodError: unknown) => {
      if (mountedRef.current) {
        setError(
          methodError instanceof Error
            ? methodError.message
            : 'Checkout payment methods are unavailable'
        )
      }
    })
  }, [
    authorization,
    countryCode,
    loadMethods,
    paymentMethodsAllowed,
    paymentMethodsSelectionOnly,
    sessionRevision,
  ])

  useEffect(() => {
    if (!activeMethodBinding || !methodResponse) {
      return
    }
    setSelectedMethodReference(
      methodReferenceForActiveExecution(flattenMethods(methodResponse), activeMethodBinding)
    )
  }, [activeMethodBinding, methodResponse])

  useEffect(() => {
    if (!authorization || !entry.returnedExecutionNo || !entry.cancellationRequested) {
      return
    }
    const currentEntry = parseCheckoutEntry()
    if (
      currentEntry.sessionNo !== entry.sessionNo ||
      currentEntry.returnedExecutionNo !== entry.returnedExecutionNo ||
      currentEntry.cancellationRequested !== entry.cancellationRequested
    ) {
      return
    }
    if (!session) return
    // PayPal 取消回跳只证明用户离开批准页，不能据此撤销仍可能生效的订阅链接。
    if (
      !session.active_execution ||
      session.active_execution.execution_no !== entry.returnedExecutionNo ||
      session.active_execution.payment_switch ||
      session.active_execution.provider === 'paypal'
    ) {
      replaceCheckoutReturnWithSessionLocation(entry.sessionNo)
      return
    }
    const operation = `return-cancel:${entry.returnedExecutionNo}`
    const idempotencyKey = getOrCreateCheckoutOperationKey(entry.sessionNo, operation)
    if (cancelReturnRef.current) {
      return
    }
    cancelReturnRef.current = cancelCheckoutExecution(
      entry.sessionNo,
      entry.returnedExecutionNo,
      authorization.sessionToken,
      idempotencyKey
    )
      .then(async () => {
        replaceCheckoutReturnWithSessionLocation(entry.sessionNo)
        clearCheckoutOperationKey(entry.sessionNo, operation)
        await refresh()
      })
      .catch((cancelError: unknown) => {
        cancelReturnRef.current = null
        setError(cancelError instanceof Error ? cancelError.message : 'Failed to cancel payment')
      })
  }, [
    authorization,
    entry.cancellationRequested,
    entry.returnedExecutionNo,
    entry.sessionNo,
    refresh,
    session,
  ])

  useEffect(() => {
    if (!authorization || isReconcilingExecution) {
      return
    }

    const currentEntry = parseCheckoutEntry()
    // 成功回跳需要服务端确认；PayPal 取消回跳只重新加载结账，不推断渠道支付状态。
    const canceledPayPalExecution = Boolean(
      entry.cancellationRequested &&
      activeExecution?.provider === 'paypal' &&
      activeExecution.execution_no === entry.returnedExecutionNo
    )
    const returnedExecutionNo =
      currentEntry.sessionNo === entry.sessionNo &&
      (!currentEntry.cancellationRequested ||
        (!canceledPayPalExecution && !activeExecution?.payment_switch)) &&
      (!activeExecution || activeExecution.execution_no === currentEntry.returnedExecutionNo)
        ? currentEntry.returnedExecutionNo
        : null
    const activeConfirmationExecutionNo = returnedExecutionNo ?? confirmationExecutionNo
    const confirmationExecutionIsActive = Boolean(
      activeConfirmationExecutionNo &&
      activeExecution?.execution_no === activeConfirmationExecutionNo
    )
    const confirmationExecutionNeedsPolling = Boolean(
      activeConfirmationExecutionNo &&
      (!confirmationExecutionIsActive || returnedExecutionNeedsConfirmation(activeExecution))
    )
    const executionNo = confirmationExecutionNeedsPolling
      ? activeConfirmationExecutionNo
      : !canceledPayPalExecution && executionNeedsPolling(activeExecution)
        ? activeExecution?.execution_no
        : undefined
    const confirmingPayment = Boolean(activeConfirmationExecutionNo)
    const pollSession =
      !executionNo &&
      !canceledPayPalExecution &&
      (sessionNeedsPolling(session) ||
        successfulReturnNeedsSessionPolling(
          session,
          activeConfirmationExecutionNo,
          activeExecution
        ))
    const pollTarget = executionNo ?? (pollSession ? `session:${entry.sessionNo}` : null)
    if (!pollTarget || paymentStatusTimedOut) {
      return
    }

    if (statusPollingRef.current.target !== pollTarget) {
      statusPollingRef.current = {
        target: pollTarget,
        attempts: 0,
        consecutiveFailures: 0,
      }
    }

    let stopped = false
    let timer: number | undefined
    let attempt = 0

    const poll = async () => {
      if (stopped) {
        return
      }
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        schedule()
        return
      }

      try {
        if (executionNo) {
          const scope = checkoutMutationAuthorizationUsable(authorization) ? 'mutation' : 'handoff'
          const token =
            scope === 'mutation' ? authorization.sessionToken : authorization.resultHandoffToken
          let execution: CheckoutExecution
          if (confirmingPayment && executionNo === activeConfirmationExecutionNo) {
            try {
              execution = await reconcileExecutionWithOperation(
                executionNo,
                `confirmation-reconcile:${executionNo}:${statusPollingRef.current.attempts}`
              )
            } catch (reconciliationError) {
              execution = await getCheckoutExecution(entry.sessionNo, executionNo, token, scope)
              if (returnedExecutionNeedsConfirmation(execution)) {
                throw reconciliationError
              }
            }
          } else {
            execution = await getCheckoutExecution(entry.sessionNo, executionNo, token, scope)
          }
          if (stopped || !mountedRef.current) {
            return
          }
          setActiveExecution(execution)
          const needsMoreConfirmation =
            confirmingPayment && executionNo === activeConfirmationExecutionNo
              ? returnedExecutionNeedsConfirmation(execution)
              : executionNeedsPolling(execution)
          if (!needsMoreConfirmation) {
            await refreshSession(execution)
            return
          }
        } else {
          const nextSession = await readSession(authorization, countryCode)
          if (stopped || !mountedRef.current) {
            return
          }
          setSession(nextSession)
          const nextActiveExecution = activeExecutionAfterSessionRefresh(
            nextSession,
            activeExecution ?? undefined
          )
          setActiveExecution(nextActiveExecution)
          if (
            !sessionNeedsPolling(nextSession) &&
            !successfulReturnNeedsSessionPolling(
              nextSession,
              activeConfirmationExecutionNo,
              nextActiveExecution
            )
          ) {
            return
          }
        }
        statusPollingRef.current.consecutiveFailures = 0
        statusPollingRef.current.attempts += 1
        attempt += 1
        if (statusPollingRef.current.attempts >= CHECKOUT_STATUS_POLL_MAX_ATTEMPTS) {
          setPaymentStatusTimedOut(true)
          return
        }
        schedule()
      } catch {
        if (stopped || !mountedRef.current) {
          return
        }
        statusPollingRef.current.consecutiveFailures += 1
        if (
          statusPollingRef.current.consecutiveFailures >= CHECKOUT_POLL_MAX_CONSECUTIVE_FAILURES
        ) {
          setPaymentStatusTimedOut(true)
          return
        }
        attempt += 1
        schedule()
      }
    }

    const schedule = () => {
      timer = window.setTimeout(() => {
        void poll()
      }, checkoutPollDelay(attempt))
    }

    schedule()
    return () => {
      stopped = true
      if (timer !== undefined) {
        window.clearTimeout(timer)
      }
    }
  }, [
    activeExecution?.execution_no,
    activeExecution?.status,
    activeExecution?.payment_switch?.mode,
    authorization,
    confirmationExecutionNo,
    countryCode,
    entry.cancellationRequested,
    entry.returnedExecutionNo,
    entry.sessionNo,
    isReconcilingExecution,
    readSession,
    reconcileExecutionWithOperation,
    refreshSession,
    paymentStatusTimedOut,
    session?.status,
  ])

  const startExecution = useCallback(
    async (
      request: Omit<CheckoutStartExecutionRequest, 'session_revision' | 'method_reference'>
    ) => {
      if (!authorization || !session || !selectedMethod) {
        throw new Error('Checkout method is unavailable')
      }
      if (startingExecutionRef.current) {
        throw new Error('Checkout execution is already starting')
      }
      if (!methodResponse || methodResponse.session_revision !== session.revision) {
        throw new Error('Checkout payment methods are stale')
      }
      if (methodResponse?.selection_only)
        throw new ApiRequestError(
          'Confirmation is required before using a new payment method',
          'CHECKOUT_PAYMENT_SWITCH_CONFIRMATION_REQUIRED'
        )
      if (selectedMethod.requires_provider_client_payload && !request.provider_client_payload) {
        throw new Error('Provider client payload is required for the selected method')
      }
      if (!selectedMethod.requires_provider_client_payload && request.provider_client_payload) {
        throw new Error('Provider client payload is not accepted for the selected method')
      }
      if (paymentSwitch?.mode === 'blocked') {
        throw new Error('CHECKOUT_PAYMENT_SWITCH_BLOCKED')
      }
      const autoReopenPayPal =
        request.abandon_previous?.approval_link_unavailable === true &&
        activeExecution?.provider === 'paypal' &&
        activeExecution.selection_status === 'active' &&
        activeExecution.execution_no === request.abandon_previous.execution_no &&
        selectedMethod.provider === 'paypal'
      if (
        paymentSwitch &&
        (!request.abandon_previous?.confirmed_unpaid ||
          request.abandon_previous.execution_no !== paymentSwitch.executionNo)
      ) {
        throw new Error('CHECKOUT_PAYMENT_SWITCH_CONFIRMATION_REQUIRED')
      }
      if (
        request.abandon_previous &&
        ((!paymentSwitch && !autoReopenPayPal) ||
          request.abandon_previous.execution_no !== activeExecution?.execution_no)
      ) {
        throw new Error('CHECKOUT_PAYMENT_SWITCH_STATE_CHANGED')
      }
      const startRequest: CheckoutStartExecutionRequest = {
        ...request,
        session_revision: methodResponse.session_revision,
        method_reference: selectedMethod.method_reference,
      }
      startingExecutionRef.current = true
      setStartingExecution(true)
      setError(null)
      try {
        const requestDigest = await digestCheckoutOperationInput(startRequest)
        const operation = `start:${requestDigest}`
        const idempotencyKey = getOrCreateCheckoutOperationKey(entry.sessionNo, operation)
        const execution = await startCheckoutExecution(
          entry.sessionNo,
          authorization.sessionToken,
          idempotencyKey,
          startRequest
        )
        clearCheckoutOperationKey(entry.sessionNo, operation)
        clearSuccessorMethodIntent(entry.sessionNo)
        setConfirmationExecutionNo(null)
        replaceCheckoutReturnWithSessionLocation(entry.sessionNo)
        statusPollingRef.current = {
          target: execution.execution_no,
          attempts: 0,
          consecutiveFailures: 0,
        }
        setPaymentStatusTimedOut(false)
        setActiveExecution(execution)
        try {
          await refreshSession(execution)
        } catch (refreshError) {
          if (mountedRef.current) {
            setError(
              refreshError instanceof Error
                ? refreshError.message
                : 'Checkout status could not be refreshed'
            )
          }
        }
        return execution
      } catch (executionError) {
        setError(
          executionError instanceof ApiRequestError &&
            (executionError.code?.startsWith('CHECKOUT_PAYMENT_SWITCH_') ||
              executionError.code === 'CHECKOUT_ELIGIBILITY_UNAVAILABLE')
            ? executionError.code
            : executionError instanceof Error
              ? executionError.message
              : 'Failed to start payment'
        )
        throw executionError
      } finally {
        startingExecutionRef.current = false
        setStartingExecution(false)
      }
    },
    [
      activeExecution?.execution_no,
      authorization,
      entry.sessionNo,
      methodResponse,
      paymentSwitch,
      refreshSession,
      selectedMethod,
      session,
    ]
  )

  const createPurchaseSuccessor = useCallback(
    async (
      sourceExecutionNo: string,
      reason: 'selection_changed' | 'link_unavailable'
    ): Promise<CheckoutPurchaseSuccessorResponse> => {
      if (
        !authorization ||
        !session ||
        !selectedMethod ||
        !activeExecution ||
        session.status !== 'open' ||
        activeExecution.execution_no !== sourceExecutionNo ||
        activeExecution.payment_switch?.mode !== 'purchase_successor' ||
        (reason === 'selection_changed' &&
          (paymentSwitch?.mode !== 'purchase_successor' ||
            paymentSwitch.executionNo !== sourceExecutionNo)) ||
        (reason === 'link_unavailable' &&
          (paymentSwitch !== null || activeExecution.status !== 'requires_action'))
      ) {
        throw new Error('CHECKOUT_PAYMENT_SWITCH_STATE_CHANGED')
      }
      if (startingExecutionRef.current) {
        throw new Error('Checkout purchase is already starting')
      }
      startingExecutionRef.current = true
      setStartingExecution(true)
      setError(null)
      const operation = `purchase-successor:${sourceExecutionNo}`
      try {
        const idempotencyKey = getOrCreateCheckoutOperationKey(entry.sessionNo, operation)
        const successor = await createCheckoutPurchaseSuccessor(
          entry.sessionNo,
          authorization.sessionToken,
          idempotencyKey,
          {
            expected_revision: session.revision,
            source_execution_no: sourceExecutionNo,
            reason,
          }
        )
        let checkoutURL: URL
        try {
          checkoutURL = new URL(successor.checkout_url)
        } catch {
          throw new Error('CHECKOUT_SUCCESSOR_URL_INVALID')
        }
        if (
          !successor.checkout_session_no?.trim() ||
          checkoutURL.origin !== window.location.origin ||
          checkoutURL.pathname !== '/checkout' ||
          checkoutURL.searchParams.get('checkout_session_no') !== successor.checkout_session_no ||
          !new URLSearchParams(checkoutURL.hash.slice(1)).get('authorization_code')
        ) {
          throw new Error('CHECKOUT_SUCCESSOR_URL_INVALID')
        }
        saveSuccessorMethodIntent(successor.checkout_session_no, {
          provider: selectedMethod.provider,
          pm_id: selectedMethod.pm_id,
          purchase_option_id: selectedMethod.purchase_option_id,
          continue_checkout: true,
        })
        return successor
      } catch (successorError) {
        const code = successorError instanceof ApiRequestError ? successorError.code : null
        setError(
          code ??
            (successorError instanceof Error
              ? successorError.message
              : 'Failed to open the new purchase')
        )
        throw successorError
      } finally {
        startingExecutionRef.current = false
        if (mountedRef.current) setStartingExecution(false)
      }
    },
    [activeExecution, authorization, entry.sessionNo, paymentSwitch, selectedMethod, session]
  )

  const cancelExecution = useCallback(
    async (executionNo: string) => {
      if (!authorization) {
        throw new Error('Checkout authorization has expired')
      }
      const operation = `cancel-execution:${executionNo}`
      const idempotencyKey = getOrCreateCheckoutOperationKey(entry.sessionNo, operation)
      await cancelCheckoutExecution(
        entry.sessionNo,
        executionNo,
        authorization.sessionToken,
        idempotencyKey
      )
      clearCheckoutOperationKey(entry.sessionNo, operation)
      await refresh()
    },
    [authorization, entry.sessionNo, refresh]
  )

  const cancelSession = useCallback(async () => {
    if (!authorization) {
      throw new Error('Checkout authorization has expired')
    }
    const operation = 'cancel-session'
    const idempotencyKey = getOrCreateCheckoutOperationKey(entry.sessionNo, operation)
    await cancelCheckoutSession(entry.sessionNo, authorization.sessionToken, idempotencyKey)
    clearCheckoutOperationKey(entry.sessionNo, operation)
    await refresh()
  }, [authorization, entry.sessionNo, refresh])

  const revise = useCallback(
    async (
      currency: string,
      quantity: number,
      couponCode: string | null,
      customerEmail: string
    ) => {
      if (!authorization || !session) {
        throw new Error('Checkout authorization has expired')
      }
      if (revisingRef.current) {
        throw new Error('Checkout revision is already in progress')
      }
      const normalizedEmail = customerEmail.trim().toLowerCase()
      const operation = `revise:${session.revision}:${currency}:${quantity}:${couponCode ?? ''}:${normalizedEmail}:${countryCode}`
      const idempotencyKey = getOrCreateCheckoutOperationKey(entry.sessionNo, operation)
      revisingRef.current = true
      setRevising(true)
      try {
        await reviseCheckoutSession(entry.sessionNo, authorization.sessionToken, idempotencyKey, {
          expected_revision: session.revision,
          currency,
          quantity,
          coupon_code: couponCode,
          billing_jurisdiction: billingJurisdictionForCountry(
            session.pricing_input.billing_jurisdiction,
            countryCode
          ),
          customer_email: normalizedEmail,
        })
        clearCheckoutOperationKey(entry.sessionNo, operation)
        await refresh()
      } finally {
        revisingRef.current = false
        if (mountedRef.current) {
          setRevising(false)
        }
      }
    },
    [authorization, countryCode, entry.sessionNo, refresh, session]
  )

  const complete = useCallback(async () => {
    if (!authorization || !session) {
      throw new Error('Checkout authorization has expired')
    }
    const operation = `complete:${session.revision}`
    const idempotencyKey = getOrCreateCheckoutOperationKey(entry.sessionNo, operation)
    await completeCheckoutSession(
      entry.sessionNo,
      authorization.sessionToken,
      idempotencyKey,
      session.revision
    )
    clearCheckoutOperationKey(entry.sessionNo, operation)
    await refresh()
  }, [authorization, entry.sessionNo, refresh, session])

  const restart = useCallback(
    async (preferredMethodReference?: string) => {
      const pendingRequest = loadCheckoutRestartIntent(entry.sessionNo)
      if (!authorization || (!pendingRequest && !customerAbandonmentAvailable(session)))
        throw new ApiRequestError(
          'Checkout restart is unavailable',
          'CHECKOUT_ABANDONMENT_NOT_SUPPORTED'
        )
      if (restartingRef.current)
        throw new ApiRequestError(
          'Checkout restart is in progress',
          'CHECKOUT_ABANDONMENT_IN_FLIGHT'
        )
      const request: CheckoutRestartRequest = pendingRequest ?? {
        expected_revision: session!.revision,
        confirmed_unpaid: true,
        confirmation_version: 'paypal_unpaid_v1',
      }
      const key = getOrCreateCheckoutOperationKey(
        entry.sessionNo,
        `restart:${request.expected_revision}`
      )
      const preferredMethod = preferredMethodReference
        ? methods.find((method) => method.method_reference === preferredMethodReference)
        : selectedMethod
      if (preferredMethodReference && !preferredMethod)
        throw new ApiRequestError(
          'Checkout method is unavailable',
          'CHECKOUT_PAYMENT_SWITCH_STATE_CHANGED'
        )
      rememberRestartMethodChoice(entry.sessionNo, preferredMethod ?? null)
      saveCheckoutRestartIntent(entry.sessionNo, request)
      setHasPendingRestart(true)
      restartingRef.current = true
      setRestarting(true)
      try {
        const response = await restartCheckoutSession(
          entry.sessionNo,
          authorization.sessionToken,
          key,
          request
        )
        const successorURL = checkoutRestartURL(response, entry.sessionNo, window.location.origin)
        copyRestartMethodChoice(entry.sessionNo, response.checkout_session_no)
        return successorURL
      } catch (failure) {
        if (checkoutRestartFailureIsTerminal(failure)) {
          const result = await getCheckoutSession(
            entry.sessionNo,
            authorization.resultHandoffToken,
            'handoff',
            countryCode
          )
          clearCheckoutRestartIntent(entry.sessionNo)
          setHasPendingRestart(false)
          if (mountedRef.current) {
            setSession(result)
            setActiveExecution(result.active_execution)
          }
        }
        throw failure
      } finally {
        restartingRef.current = false
        if (mountedRef.current) setRestarting(false)
      }
    },
    [authorization, countryCode, entry.sessionNo, methods, session, selectedMethod]
  )

  const issueResultAuthorization = useCallback(async () => {
    if (!authorization || !session || !sessionIsTerminal(session)) {
      throw new Error('Checkout result is not available')
    }
    const operation = `result-authorization:${session.revision}`
    const idempotencyKey = getOrCreateCheckoutOperationKey(entry.sessionNo, operation)
    const response = await issueCheckoutResultAuthorization(
      entry.sessionNo,
      authorization.resultHandoffToken,
      idempotencyKey,
      session.revision
    )
    clearCheckoutOperationKey(entry.sessionNo, operation)
    return response.authorization_code
  }, [authorization, entry.sessionNo, session])

  return {
    entry,
    authorization,
    session,
    methods,
    selectedMethod,
    activeExecution,
    paymentSwitch,
    countryCode,
    currencyOptions,
    isInitializing,
    isLoadingMethods,
    isStartingExecution,
    isRevising,
    isRestarting,
    hasPendingRestart,
    restart,
    paymentStatusTimedOut,
    error,
    selectMethod: (methodReference: string) => {
      clearSuccessorMethodIntent(entry.sessionNo)
      setSelectedMethodReference(methodReference)
    },
    startExecution,
    createPurchaseSuccessor,
    cancelExecution,
    cancelSession,
    refresh,
    retryPaymentStatusPolling,
    reconcileExecution,
    revise,
    complete,
    issueResultAuthorization,
  }
}
